<template>
  <PartnersLayout>
    <template #header>
      <PartnersHeader
        title="내 고객 보관함"
        subtitle="엑셀처럼 고객을 관리하세요"
      />
    </template>

    <div class="px-4 lg:px-6 pb-6">
      <div class="max-w-6xl mx-auto">
        <!-- 상단 요약 + 엑셀 다운로드 -->
        <div class="flex items-center justify-between mb-4">
          <div class="flex items-center gap-4">
            <div class="bg-white border border-slate-200 rounded-xl px-4 py-2">
              <p class="text-[11px] text-slate-400">총 고객</p>
              <p class="text-[24px] font-black text-blue-600 leading-none">{{ myCustomers.length }}</p>
            </div>
          </div>
          <button
            @click="exportToExcel"
            :disabled="myCustomers.length === 0"
            class="flex items-center gap-2 px-4 py-2.5 bg-blue-600 text-white text-[13px] font-bold rounded-xl hover:bg-blue-700 transition disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"/>
            </svg>
            엑셀 다운로드
          </button>
        </div>

        <!-- 필터/검색 바 -->
        <div class="bg-white rounded-2xl p-4 mb-4 shadow-sm">
          <div class="flex flex-wrap items-center gap-4">
            <!-- 상태 필터 -->
            <div class="flex items-center gap-2">
              <span class="text-sm text-slate-500">상태:</span>
              <select
                v-model="filterStatus"
                class="px-3 py-2 bg-slate-50 border border-slate-200 rounded-lg text-sm outline-none focus:border-blue-600"
              >
                <option value="all">전체</option>
                <option value="consulting">상담중</option>
                <option value="scheduled">계약예정</option>
                <option value="completed">계약완료</option>
                <option value="canceled">취소/포기</option>
              </select>
            </div>

            <!-- 검색 -->
            <div class="flex-1 min-w-[200px]">
              <div class="relative">
                <svg class="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"/>
                </svg>
                <input
                  v-model="searchQuery"
                  type="text"
                  placeholder="이름 또는 연락처로 검색..."
                  class="w-full pl-10 pr-4 py-2 bg-slate-50 border border-slate-200 rounded-lg text-sm outline-none focus:border-blue-600"
                />
              </div>
            </div>

            <!-- 정렬 -->
            <div class="flex items-center gap-2">
              <span class="text-sm text-slate-500">정렬:</span>
              <select
                v-model="sortBy"
                class="px-3 py-2 bg-slate-50 border border-slate-200 rounded-lg text-sm outline-none focus:border-blue-600"
              >
                <option value="newest">최신순</option>
                <option value="oldest">오래된순</option>
                <option value="name">이름순</option>
              </select>
            </div>
          </div>
        </div>

        <!-- 로딩 -->
        <div v-if="loading" class="bg-white rounded-2xl p-12 text-center shadow-sm">
          <div class="w-10 h-10 border-4 border-blue-600 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p class="text-slate-500">데이터를 불러오는 중...</p>
        </div>

        <!-- 빈 상태 -->
        <div v-else-if="filteredCustomers.length === 0" class="bg-white rounded-2xl p-12 text-center shadow-sm">
          <div class="w-20 h-20 bg-slate-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <svg class="w-10 h-10 text-slate-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z"/>
            </svg>
          </div>
          <h3 class="text-lg font-semibold text-slate-600 mb-2">
            {{ searchQuery || filterStatus !== 'all' ? '검색 결과가 없습니다' : '아직 고객이 없습니다' }}
          </h3>
          <p class="text-slate-400">고객과 상담을 완료하면 여기에 표시됩니다.</p>
        </div>

        <!-- 엑셀 스타일 테이블 -->
        <div v-else class="bg-white rounded-2xl shadow-sm overflow-hidden">
          <div class="overflow-x-auto">
            <table class="w-full">
              <thead>
                <tr class="bg-slate-50 border-b border-slate-200">
                  <th class="table-header w-12">#</th>
                  <th class="table-header cursor-pointer hover:bg-slate-100" @click="toggleSort('name')">
                    <div class="flex items-center gap-1">
                      이름
                      <svg v-if="sortBy === 'name'" class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7"/>
                      </svg>
                    </div>
                  </th>
                  <th class="table-header">연락처</th>
                  <th class="table-header">현장</th>
                  <th class="table-header">상태</th>
                  <th class="table-header">메모</th>
                  <th class="table-header cursor-pointer hover:bg-slate-100" @click="toggleSort('newest')">
                    <div class="flex items-center gap-1">
                      등록일
                      <svg v-if="sortBy === 'newest' || sortBy === 'oldest'" class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" :d="sortBy === 'newest' ? 'M19 9l-7 7-7-7' : 'M5 15l7-7 7 7'"/>
                      </svg>
                    </div>
                  </th>
                  <th class="table-header w-24">액션</th>
                </tr>
              </thead>
              <tbody>
                <tr
                  v-for="(customer, index) in filteredCustomers"
                  :key="customer.id"
                  class="table-row"
                >
                  <td class="table-cell text-center text-slate-400">{{ index + 1 }}</td>
                  <td class="table-cell font-medium text-slate-800">
                    <div class="flex items-center gap-2">
                      {{ customer.customerName }}
                      <span v-if="customer.fromCampaign" class="px-1.5 py-0.5 bg-blue-100 text-blue-600 text-xs rounded">
                        캠페인
                      </span>
                    </div>
                  </td>
                  <td class="table-cell">
                    <a :href="`tel:${customer.customerPhone}`" class="text-blue-600 hover:underline">
                      {{ customer.customerPhone }}
                    </a>
                  </td>
                  <td class="table-cell">
                    <span class="px-2 py-1 bg-blue-50 text-blue-600 text-xs font-medium rounded">
                      {{ customer.propertyName }}
                    </span>
                  </td>
                  <td class="table-cell">
                    <select
                      :value="customer.consultStatus || 'consulting'"
                      @change="updateConsultStatus(customer, $event.target.value)"
                      :class="[
                        'status-select',
                        getStatusColor(customer.consultStatus || 'consulting')
                      ]"
                    >
                      <option value="consulting">상담중</option>
                      <option value="scheduled">계약예정</option>
                      <option value="completed">계약완료</option>
                      <option value="canceled">취소/포기</option>
                    </select>
                  </td>
                  <td class="table-cell">
                    <div class="memo-cell" @click="openMemoModal(customer)">
                      <span v-if="customer.memo" class="text-slate-600 truncate">{{ customer.memo }}</span>
                      <span v-else class="text-slate-400 italic">메모 추가...</span>
                      <svg class="w-4 h-4 text-slate-400 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z"/>
                      </svg>
                    </div>
                  </td>
                  <td class="table-cell text-slate-500 text-sm">
                    {{ formatDate(customer.contactedAt || customer.pickedAt || customer.createdAt) }}
                  </td>
                  <td class="table-cell">
                    <div class="flex items-center gap-1">
                      <button
                        @click="callCustomer(customer)"
                        class="p-2 bg-green-500 text-white rounded-lg hover:bg-green-600"
                        title="전화걸기"
                      >
                        <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z"/>
                        </svg>
                      </button>
                    </div>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>

          <!-- 테이블 푸터 -->
          <div class="px-4 py-3 bg-slate-50 border-t border-slate-200 text-sm text-slate-500">
            총 {{ filteredCustomers.length }}명의 고객
            <span v-if="searchQuery || filterStatus !== 'all'">
              (필터링됨: 전체 {{ myCustomers.length }}명 중)
            </span>
          </div>
        </div>

      </div>
    </div>

    <!-- 메모 수정 모달 -->
    <div v-if="showMemoModal" class="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4">
      <div class="bg-white rounded-2xl w-full max-w-lg overflow-hidden">
        <div class="flex items-center justify-between px-6 py-4 border-b border-slate-100">
          <h3 class="text-lg font-semibold text-slate-800">상담 메모</h3>
          <button class="p-1 text-slate-400 hover:text-slate-600" @click="closeMemoModal">
            <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"/>
            </svg>
          </button>
        </div>
        <div class="p-6">
          <div class="flex items-center gap-3 mb-4 pb-4 border-b border-slate-100">
            <span class="font-semibold text-slate-800">{{ editingCustomer?.customerName }}</span>
            <span class="text-sm text-slate-500">{{ editingCustomer?.customerPhone }}</span>
          </div>
          <textarea
            v-model="memoText"
            class="w-full p-4 bg-slate-50 border border-slate-200 rounded-xl outline-none resize-none focus:border-blue-600 focus:ring-2 focus:ring-blue-600/20"
            placeholder="상담 내용, 고객 요구사항, 다음 연락 계획 등을 기록해주세요."
            rows="6"
          ></textarea>
        </div>
        <div class="flex gap-3 px-6 py-4 bg-slate-50">
          <button
            class="flex-1 py-3 border border-slate-300 text-slate-600 font-medium rounded-lg hover:bg-slate-100 transition"
            @click="closeMemoModal"
          >
            취소
          </button>
          <button
            class="flex-1 py-3 bg-blue-600 text-white font-medium rounded-lg hover:bg-blue-700 transition disabled:bg-slate-300"
            @click="saveMemo"
            :disabled="savingMemo"
          >
            {{ savingMemo ? '저장 중...' : '저장' }}
          </button>
        </div>
      </div>
    </div>
  </PartnersLayout>
</template>

<script>
import { ref, computed, onMounted } from 'vue'
import { collection, query, where, getDocs, doc, updateDoc, or } from 'firebase/firestore'
import { db } from '@/firebase/firebase'
import { useAuth } from '@/composables/common/useAuth'
import PartnersLayout from '@/components/partners/common/PartnersLayout.vue'
import PartnersHeader from '@/components/partners/common/PartnersHeader.vue'

export default {
  name: 'MyCustomersView',
  components: {
    PartnersLayout,
    PartnersHeader
  },
  setup() {
    const { user } = useAuth()
    const loading = ref(true)
    const customers = ref([])

    // 필터/검색/정렬
    const filterStatus = ref('all')
    const searchQuery = ref('')
    const sortBy = ref('newest')

    // 메모 모달
    const showMemoModal = ref(false)
    const editingCustomer = ref(null)
    const memoText = ref('')
    const savingMemo = ref(false)

    const myCustomers = computed(() => {
      return customers.value.filter(c =>
        c.status === 'contacted' ||
        (c.status === 'picked' && c.pickedBy === user.value?.uid)
      )
    })

    const filteredCustomers = computed(() => {
      let result = [...myCustomers.value]

      // 상태 필터
      if (filterStatus.value !== 'all') {
        result = result.filter(c =>
          (c.consultStatus || 'consulting') === filterStatus.value
        )
      }

      // 검색
      if (searchQuery.value.trim()) {
        const q = searchQuery.value.toLowerCase()
        result = result.filter(c =>
          c.customerName.toLowerCase().includes(q) ||
          c.customerPhone.includes(q)
        )
      }

      // 정렬
      result.sort((a, b) => {
        if (sortBy.value === 'name') {
          return a.customerName.localeCompare(b.customerName, 'ko')
        } else if (sortBy.value === 'oldest') {
          return new Date(a.createdAt) - new Date(b.createdAt)
        } else {
          // newest
          return new Date(b.createdAt) - new Date(a.createdAt)
        }
      })

      return result
    })

    const loadCustomers = async () => {
      if (!user.value) return

      try {
        loading.value = true

        const q = query(
          collection(db, 'consultationRequests'),
          or(
            where('partnerId', '==', user.value.uid),
            where('pickedBy', '==', user.value.uid)
          )
        )

        const snapshot = await getDocs(q)
        customers.value = snapshot.docs
          .map(doc => ({
            id: doc.id,
            ...doc.data()
          }))
          .filter(c => c.status === 'contacted' || (c.status === 'picked' && c.pickedBy === user.value.uid))
      } catch (error) {
        console.error('고객 로드 실패:', error)
      } finally {
        loading.value = false
      }
    }

    const formatDate = (dateStr) => {
      if (!dateStr) return '-'
      const date = new Date(dateStr)
      return date.toLocaleDateString('ko-KR', {
        year: '2-digit',
        month: '2-digit',
        day: '2-digit'
      })
    }

    const getStatusColor = (status) => {
      const colors = {
        'consulting': 'status-consulting',
        'scheduled': 'status-scheduled',
        'completed': 'status-completed',
        'canceled': 'status-canceled'
      }
      return colors[status] || 'status-consulting'
    }

    const toggleSort = (type) => {
      if (type === 'name') {
        sortBy.value = sortBy.value === 'name' ? 'newest' : 'name'
      } else {
        sortBy.value = sortBy.value === 'newest' ? 'oldest' : 'newest'
      }
    }

    const callCustomer = (customer) => {
      window.location.href = `tel:${customer.customerPhone}`
    }

    const updateConsultStatus = async (customer, newStatus) => {
      try {
        await updateDoc(doc(db, 'consultationRequests', customer.id), {
          consultStatus: newStatus,
          updatedAt: new Date().toISOString()
        })

        const index = customers.value.findIndex(c => c.id === customer.id)
        if (index !== -1) {
          customers.value[index].consultStatus = newStatus
        }
      } catch (error) {
        console.error('상태 업데이트 실패:', error)
        alert('상태 변경에 실패했습니다.')
      }
    }

    const openMemoModal = (customer) => {
      editingCustomer.value = customer
      memoText.value = customer.memo || ''
      showMemoModal.value = true
    }

    const closeMemoModal = () => {
      showMemoModal.value = false
      editingCustomer.value = null
      memoText.value = ''
    }

    const saveMemo = async () => {
      if (!editingCustomer.value) return

      savingMemo.value = true

      try {
        await updateDoc(doc(db, 'consultationRequests', editingCustomer.value.id), {
          memo: memoText.value,
          updatedAt: new Date().toISOString()
        })

        const index = customers.value.findIndex(c => c.id === editingCustomer.value.id)
        if (index !== -1) {
          customers.value[index].memo = memoText.value
        }

        closeMemoModal()
      } catch (error) {
        console.error('메모 저장 실패:', error)
        alert('메모 저장에 실패했습니다.')
      } finally {
        savingMemo.value = false
      }
    }

    // 엑셀 다운로드
    const exportToExcel = async () => {
      try {
        // xlsx 라이브러리 동적 로드
        const XLSX = await import('xlsx')

        const data = filteredCustomers.value.map((c, index) => ({
          '번호': index + 1,
          '이름': c.customerName,
          '연락처': c.customerPhone,
          '현장': c.propertyName,
          '상태': getStatusLabel(c.consultStatus || 'consulting'),
          '메모': c.memo || '',
          '등록일': formatDate(c.contactedAt || c.pickedAt || c.createdAt),
          '캠페인': c.fromCampaign ? 'O' : ''
        }))

        const ws = XLSX.utils.json_to_sheet(data)
        const wb = XLSX.utils.book_new()
        XLSX.utils.book_append_sheet(wb, ws, '내 고객')

        // 컬럼 너비 설정
        ws['!cols'] = [
          { wch: 5 },   // 번호
          { wch: 10 },  // 이름
          { wch: 15 },  // 연락처
          { wch: 20 },  // 현장
          { wch: 10 },  // 상태
          { wch: 40 },  // 메모
          { wch: 12 },  // 등록일
          { wch: 8 }    // 캠페인
        ]

        const today = new Date().toISOString().slice(0, 10)
        XLSX.writeFile(wb, `내고객_${today}.xlsx`)
      } catch (error) {
        console.error('엑셀 다운로드 실패:', error)
        alert('엑셀 다운로드에 실패했습니다. xlsx 라이브러리가 설치되어 있는지 확인해주세요.')
      }
    }

    const getStatusLabel = (status) => {
      const labels = {
        'consulting': '상담중',
        'scheduled': '계약예정',
        'completed': '계약완료',
        'canceled': '취소/포기'
      }
      return labels[status] || '상담중'
    }

    onMounted(() => {
      loadCustomers()
    })

    return {
      loading,
      myCustomers,
      filteredCustomers,
      filterStatus,
      searchQuery,
      sortBy,
      formatDate,
      getStatusColor,
      toggleSort,
      callCustomer,
      updateConsultStatus,
      showMemoModal,
      editingCustomer,
      memoText,
      savingMemo,
      openMemoModal,
      closeMemoModal,
      saveMemo,
      exportToExcel
    }
  }
}
</script>

<style scoped>
@reference "tailwindcss";

/* Table Styles */
.table-header {
  @apply px-4 py-3 text-left text-sm font-semibold text-slate-600;
}

.table-row {
  @apply border-b border-slate-100 hover:bg-slate-50 transition;
}

.table-cell {
  @apply px-4 py-3;
}

.memo-cell {
  @apply flex items-center gap-2 cursor-pointer hover:bg-slate-100 rounded px-2 py-1 -mx-2 max-w-[200px];
}

/* Status Select Styles */
.status-select {
  @apply px-2 py-1 text-xs font-medium rounded-lg border-0 outline-none cursor-pointer;
}

.status-consulting {
  @apply bg-blue-50 text-blue-600;
}

.status-scheduled {
  @apply bg-amber-50 text-amber-600;
}

.status-completed {
  @apply bg-green-50 text-green-600;
}

.status-canceled {
  @apply bg-slate-100 text-slate-500;
}
</style>
